import React from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useParams } from "react-router-dom";
import agent from "../../../agent";
import { history } from "../../../store";
import { getCrossDockingDetailMock } from "./mockData";
import "./details.css";

const WEEK_DAYS = {
  1: "Понедельник",
  2: "Вторник",
  3: "Среда",
  4: "Четверг",
  5: "Пятница",
  6: "Суббота",
  7: "Воскресенье",
};

const EMPTY_VALUE = "—";

const CrossDockingTariffDetails = () => {
  const { id } = useParams();
  const toastRef = React.useRef(null);
  const [details, setDetails] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isMockData, setIsMockData] = React.useState(false);

  const showMessage = React.useCallback((severity, summary, detail, life = 7000) => {
    toastRef.current?.show({ severity, summary, detail, life });
  }, []);

  const fetchDetails = React.useCallback(async () => {
    if (!id) {
      return;
    }

    setIsLoading(true);
    setIsMockData(false);

    try {
      const payload = await agent.CrossDocking.getDetail(id);
      setDetails({
        ...payload,
        id: payload?.id ?? (Number(id) || id),
      });
    } catch (err) {
      setIsMockData(true);
      setDetails(getCrossDockingDetailMock(id));
      showMessage("warn", "Показаны мок-данные", `Не удалось загрузить детали. ${agent.handleError(err)}`);
    } finally {
      setIsLoading(false);
    }
  }, [id, showMessage]);

  React.useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const scheduleRows = React.useMemo(() => {
    const rawTimeJson = details?.operationMode?.timeJson;
    let parsed = [];

    if (Array.isArray(rawTimeJson)) {
      parsed = rawTimeJson;
    } else if (typeof rawTimeJson === "string" && rawTimeJson.trim()) {
      try {
        parsed = JSON.parse(rawTimeJson);
      } catch (e) {
        parsed = [];
      }
    }

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item) => {
        const day = Number(item?.day);
        const isWeekend = Boolean(item?.weekend);
        return {
          id: `${day}-${item?.start || ""}-${item?.end || ""}`,
          day,
          dayLabel: WEEK_DAYS[day] || `День ${day}`,
          statusLabel: isWeekend ? "Выходной" : "Рабочий",
          startLabel: item?.start || EMPTY_VALUE,
          endLabel: item?.end || EMPTY_VALUE,
          isWeekend,
        };
      })
      .sort((a, b) => a.day - b.day);
  }, [details]);

  const infoItems = React.useMemo(() => ([
    { label: "Идентификатор", value: details?.id ?? EMPTY_VALUE },
    { label: "Название", value: details?.name || EMPTY_VALUE },
    { label: "КАТО", value: details?.kato ?? EMPTY_VALUE },
    { label: "Населенный пункт", value: details?.locality ?? EMPTY_VALUE },
    { label: "Адрес", value: details?.address || EMPTY_VALUE },
    { label: "Телефон", value: details?.contractTel || details?.contactTel || EMPTY_VALUE },
  ]), [details]);

  return (
    <div className="cross-docking-details">
      <Toast ref={toastRef} />

      <div className="cross-docking-details__header">
        <div>
          <h1>Детали кросс-докинга</h1>
          <p>Карточка точки кросс-докинга и график работы.</p>
        </div>
        <div className="cross-docking-details__actions">
          <Button
            label="Назад в реестр"
            icon="pi pi-arrow-left"
            className="p-button-text"
            onClick={() => history.push("/cross-docking-tarrifs")}
          />
          <Button
            label="Обновить"
            icon="pi pi-refresh"
            className="p-button-outlined"
            onClick={fetchDetails}
            loading={isLoading}
          />
        </div>
      </div>

      {isLoading && !details ? (
        <div className="cross-docking-details__loading">
          <Skeleton width="100%" height="180px" className="p-mb-3" />
          <Skeleton width="100%" height="300px" />
        </div>
      ) : (
        <>
          <div className="cross-docking-details__summary">
            <div className="cross-docking-details__summary-title">
              Точка {details?.locality || EMPTY_VALUE}
            </div>
            <div className="cross-docking-details__summary-tags">
              <Tag
                value={details?.isActive ? "Активна" : "Неактивна"}
                severity={details?.isActive ? "success" : "danger"}
              />
              {isMockData ? <Tag value="Мок-данные" severity="warning" /> : null}
            </div>
          </div>

          <div className="cross-docking-details__meta-grid">
            {infoItems.map((item) => (
              <div className="cross-docking-details__meta-card" key={item.label}>
                <div className="cross-docking-details__meta-label">{item.label}</div>
                <div className="cross-docking-details__meta-value">{item.value}</div>
              </div>
            ))}
          </div>

          <div className="cross-docking-details__panel">
            <div className="cross-docking-details__panel-header">
              <h2>Режим работы</h2>
              <Tag
                value={details?.operationMode?.isActive ? "Режим активен" : "Режим неактивен"}
                severity={details?.operationMode?.isActive ? "success" : "warning"}
              />
            </div>

            <p className="cross-docking-details__panel-text">
              {details?.operationMode?.time || "Режим не указан"}
            </p>

            {details?.operationMode?.message ? (
              <div className="cross-docking-details__message">{details.operationMode.message}</div>
            ) : null}

            <DataTable
              value={scheduleRows}
              dataKey="id"
              emptyMessage="Детализированное расписание не найдено"
              rowHover
              size="small"
              stripedRows
              showGridlines
              responsiveLayout="scroll"
            >
              <Column field="dayLabel" header="День недели" />
              <Column field="statusLabel" header="Статус" />
              <Column field="startLabel" header="Начало" />
              <Column field="endLabel" header="Окончание" />
            </DataTable>
          </div>
        </>
      )}
    </div>
  );
};

export default CrossDockingTariffDetails;
